import React, { useEffect, useRef, useState } from 'react';
import { assinarPlano, escolherPlano, IdDoPlano, SituacaoDoPlano, usarCupom } from '../services/api';
import { abrirAbaDePagamento } from '../utils/abaDePagamento';
import './TelaDePlanos.css';

/**
 * A tela de planos (20/10) — `docs/designs/precificacao.md`.
 *
 * O que quem está sem plano vê no lugar do app. O desenho é o do rascunho
 * aprovado por ela (`docs/designs/rascunho-planos-conheca.html`): três
 * cartões, o Completo no meio e já escolhido, com a fita; o Anual com
 * "33% a menos"; a garantia dentro de "Pagamento e devolução", fechado.
 *
 * Os preços vêm do SERVIDOR (`/api/planos`), nunca escritos aqui: preço de
 * plano pago não pode ter duas fontes.
 *
 * Escolher não libera nada: registra a escolha e mostra como pagar. Quem
 * libera é o pagamento.
 */

const reais = (centavos: number): string => `R$ ${(centavos / 100).toFixed(2).replace('.', ',')}`;

/** O que cada plano inclui. "Meu plano" mostra a mesma lista, para comparar antes de trocar. */
export const ITENS: Record<IdDoPlano, string[]> = {
  // Itens curtos, que se leem de relance; a projeção, que é o centro do app, vem primeiro (22/10).
  essencial: [
    'Quanto sobra em cada mês, com parcelas e contas fixas',
    'Lance por frase, áudio, foto ou print da fatura',
    'Lançamentos pelo WhatsApp',
    'Finn, seu coach financeiro, com os seus números',
    'Análise do mês e newsletter com dicas para melhorar',
    'Metas, viagens em outra moeda, pontos e milhas',
  ],
  // 28/10: 3 bancos no Completo, até 5 com banco a mais.
  completo: [
    '3 bancos conectados, até 5 com banco a mais',
    'Gastos entram sozinhos, já com categoria',
    'Fatura paga reconhecida, sem duplicar',
    'Finn avisa antes do susto',
  ],
  essencial_anual: [
    'Um pagamento por ano, sem pensar nisso todo mês',
  ],
};

/**
 * Por que no FinTracking é diferente (22/10): o que o app faz e quem só mostra extrato não
 * faz. Sem citar concorrente pelo nome: comparação nomeada precisa de prova.
 * Só aqui: a /conheca tinha uma cópia e tirou em 28/10, porque lá ela resumia
 * a página que a pessoa acabou de ler.
 */
export const MOTIVOS: { cena: Cena; titulo: string; texto: string }[] = [
  { cena: 'futuro', titulo: 'Mostra o futuro, não só o passado', texto: 'Quanto vai sobrar em cada um dos próximos meses, com as parcelas e as contas fixas. Não só para onde o dinheiro já foi.' },
  { cena: 'coach', titulo: 'Um coach, não um robô de respostas', texto: 'O Finn usa os seus números, explica com fonte oficial e cobra o que você combinou.' },
  { cena: 'lanca', titulo: 'Lança do jeito mais rápido', texto: 'Uma frase, um áudio, uma foto ou o print da fatura inteira de uma vez.' },
  { cena: 'cartao', titulo: 'Faz o seu cartão render', texto: 'As vantagens da bandeira que você já tem, a conta do milheiro e qual cartão usar em cada compra.' },
  { cena: 'email', titulo: 'Te mantém por dentro, sem abrir o app', texto: 'Todo dia útil, se você quiser, uma newsletter que lê os seus números e diz o que melhorar. Todo mês, a análise do que mudou.' },
];

type Cena = 'futuro' | 'coach' | 'lanca' | 'cartao' | 'email';

/** Os meses da cena "futuro": um deles fica no vermelho, que é o que o app avisa antes. */
const MESES: [string, number][] = [['nov', 820], ['dez', 410], ['jan', 640], ['fev', 150], ['mar', -230], ['abr', 380]];

/** Uma cena pequena do app em cada motivo, para mostrar em vez de só dizer. */
const CenaDoMotivo: React.FC<{ cena: Cena }> = ({ cena }) => {
  if (cena === 'futuro') {
    return (
      <div className="tp-cena tp-cena-meses" aria-hidden="true">
        {MESES.map(([mes, v]) => (
          <div key={mes} className={`tp-mes${v < 0 ? ' tp-mes-neg' : ''}`}>
            <span className="tp-mes-barra" style={{ height: `${Math.max(12, Math.abs(v) / 820 * 100)}%` }} />
            <span className="tp-mes-nome">{mes}</span>
          </div>
        ))}
        <span className="tp-mes-aviso">março: −R$ 230</span>
      </div>
    );
  }
  if (cena === 'coach') {
    return (
      <div className="tp-cena tp-cena-chat" aria-hidden="true">
        <span className="tp-balao tp-balao-eu">Posso comprar um sofá de 3.000?</span>
        <span className="tp-balao tp-balao-finn"><strong>Finn</strong> Em 10x, março fica no vermelho. Em 12x, não.</span>
      </div>
    );
  }
  if (cena === 'lanca') {
    return (
      <div className="tp-cena tp-cena-lanca" aria-hidden="true">
        <span className="tp-campo">uber 32 no crédito<em>✓ Transporte</em></span>
        <span className="tp-fichas"><span>Frase</span><span>Áudio</span><span>Foto</span><span>Print da fatura</span></span>
      </div>
    );
  }
  if (cena === 'email') {
    return (
      <div className="tp-cena tp-cena-email" aria-hidden="true">
        <span className="tp-email">
          <span className="tp-email-topo"><strong>FinTracking</strong><span>7:00</span></span>
          <span className="tp-email-assunto">Seu dinheiro hoje</span>
          <span className="tp-email-dica">Delivery subiu 40%. Um teto de R$ 300 resolve →</span>
        </span>
        <span className="tp-email-mes">Análise de outubro</span>
      </div>
    );
  }
  return (
    <div className="tp-cena tp-cena-cartao" aria-hidden="true">
      <span className="tp-cartao"><span className="tp-cartao-chip" /><span className="tp-cartao-nome">Platinum</span></span>
      <span className="tp-cartao-dicas">
        <span>Seguro viagem incluso</span>
        <span>10 mil pts ≈ <strong>R$ 250</strong></span>
      </span>
    </div>
  );
};


/** A ordem dos cartões: o Completo no meio. No celular ele sobe para o topo (CSS). */
const ORDEM: IdDoPlano[] = ['essencial', 'completo', 'essencial_anual'];

const Check = () => (
  <svg className="tp-check" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
);

/**
 * Os motivos, em carrossel (22/10; no computador também, a pedido dela). O card
 * do meio em destaque, os vizinhos apagados dos lados. Anda sozinho a cada
 * 4,5 s enquanto está na tela; mexer o mouse em cima, clicar, arrastar ou
 * rolar de lado PAUSA, e ele volta a andar 8 s depois do último gesto. Rolar a
 * página com a roda por cima NÃO conta. Com "reduzir movimento", não anda.
 * Rola só a LISTA, nunca a página (a mesma armadilha do carrossel da /conheca).
 *
 * Até 28/10 o toque parava DE VEZ: no celular a lista ocupa a largura da tela,
 * e rolar a página com o dedo passando por cima já desligava o carrossel para
 * sempre — ela viu "não está mudando sozinho". E o mouse PARADO em cima
 * pausava (mouseenter): rolar a página com a roda deixa o carrossel embaixo do
 * cursor, e no computador ele quase nunca andava. Agora só o mouse que se mexe.
 */
const PASSO_DO_CARROSSEL = 4500;
const RETOMA_DEPOIS_DO_TOQUE = 8000;

/**
 * Infinito (28/10, pedido dela: "quando chega no final ele para"). A lista tem
 * os motivos TRÊS vezes, e o carrossel mora na do meio. Andar sempre vai para
 * a frente; ao assentar fora da volta do meio, ele salta em silêncio para o
 * mesmo card na volta do meio — o conteúdo é igual, então o salto não se vê.
 * As cópias de fora são `aria-hidden`: o leitor de tela lê cada motivo uma vez.
 * As setas saíram no mesmo dia; ficam as bolinhas.
 */
const N_MOTIVOS = MOTIVOS.length;
const VOLTAS = [0, 1, 2];

const Motivos: React.FC = () => {
  const lista = useRef<HTMLDivElement>(null);
  const [ativo, setAtivo] = useState(N_MOTIVOS);
  const atual = useRef(N_MOTIVOS);
  const pausaAte = useRef(0);
  const naTela = useRef(false);
  // A rolagem que o próprio carrossel pediu não muda o card escolhido no meio do caminho.
  const doCarrossel = useRef(false);

  const ir = (i: number, suave = true) => {
    const l = lista.current;
    const card = l?.children[i] as HTMLElement | undefined;
    if (!l || !card) return;
    atual.current = i;
    setAtivo(i);
    doCarrossel.current = suave;
    // O salto sem animação também apaga a transição dos cards por um instante:
    // senão o destaque "anda" de um card para a cópia dele.
    if (!suave) {
      l.classList.add('tp-sem-transicao');
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => l.classList.remove('tp-sem-transicao')));
    }
    l.scrollTo({ left: card.offsetLeft - (l.clientWidth - card.offsetWidth) / 2, behavior: suave ? 'smooth' : 'auto' });
  };

  /** Fora da volta do meio: salta para o mesmo card nela, sem animação. */
  const voltarAoMeio = () => {
    const i = atual.current;
    if (i >= N_MOTIVOS && i < 2 * N_MOTIVOS) return;
    ir((i % N_MOTIVOS) + N_MOTIVOS, false);
  };

  // Começa na volta do meio, para haver card dos dois lados.
  useEffect(() => { ir(N_MOTIVOS, false); }, []);

  // Quem rolou com o dedo: o card mais perto do meio vira o escolhido.
  useEffect(() => {
    const l = lista.current;
    if (!l) return;
    let assentar: number | undefined;
    const aoRolar = () => {
      window.clearTimeout(assentar);
      assentar = window.setTimeout(() => {
        if (doCarrossel.current) { doCarrossel.current = false; voltarAoMeio(); return; }
        const meio = l.scrollLeft + l.clientWidth / 2;
        let perto = 0, menor = Infinity;
        Array.from(l.children).forEach((c, k) => {
          const el = c as HTMLElement;
          const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - meio);
          if (d < menor) { menor = d; perto = k; }
        });
        atual.current = perto;
        setAtivo(perto);
        voltarAoMeio();
      }, 120);
    };
    l.addEventListener('scroll', aoRolar, { passive: true });
    return () => { l.removeEventListener('scroll', aoRolar); window.clearTimeout(assentar); };
    // Uma vez só: `voltarAoMeio` e `ir` leem refs, nada que mude entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anda sozinho, sempre para a frente: só na tela, e pausado logo depois de um gesto.
  useEffect(() => {
    const l = lista.current;
    if (!l || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const olho = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([e]) => { naTela.current = e.isIntersecting; }, { threshold: 0.6 })
      : null;
    olho?.observe(l);
    const relogio = window.setInterval(() => {
      if (Date.now() < pausaAte.current || !naTela.current || document.hidden) return;
      ir(atual.current + 1);
    }, PASSO_DO_CARROSSEL);
    return () => { window.clearInterval(relogio); olho?.disconnect(); };
  }, []);

  const parar = () => { pausaAte.current = Date.now() + RETOMA_DEPOIS_DO_TOQUE; };
  const aoRodar = (e: React.WheelEvent) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) parar(); };
  const doMotivo = ativo % N_MOTIVOS;

  return (
    <section className="tp-motivos" aria-labelledby="tp-motivos-titulo">
      <h2 id="tp-motivos-titulo">Por que no FinTracking é diferente</h2>
      <div className="tp-motivos-grade" ref={lista} onPointerDown={parar} onTouchStart={parar} onWheel={aoRodar}
        onMouseMove={parar}>
        {VOLTAS.flatMap(v => MOTIVOS.map((m, i) => {
          const k = v * N_MOTIVOS + i;
          return (
            <div key={`${v}-${m.titulo}`} aria-hidden={v === 1 ? undefined : true}
              className={`tp-motivo${k === ativo ? ' tp-motivo-ativo' : ''}`}>
              <CenaDoMotivo cena={m.cena} />
              <h3>{m.titulo}</h3>
              <p>{m.texto}</p>
            </div>
          );
        }))}
      </div>
      <div className="tp-motivos-pontos">
        {MOTIVOS.map((m, i) => (
          <button key={m.titulo} type="button" className="tp-ponto" tabIndex={-1} aria-hidden="true"
            aria-current={i === doMotivo ? 'true' : undefined}
            onClick={() => { parar(); ir(N_MOTIVOS + i); }} />
        ))}
      </div>
    </section>
  );
};

/**
 * "Tem um cupom?" (27/10): fechado, discreto, embaixo dos planos. O cupom
 * libera na hora, sem pagamento: a situação nova abre o app sozinha.
 */
const Cupom: React.FC<{ onUsado: (s: SituacaoDoPlano) => void }> = ({ onUsado }) => {
  const [aberto, setAberto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const aplicar = async (e: React.FormEvent) => {
    e.preventDefault();
    setIndo(true); setErro(null);
    try {
      onUsado(await usarCupom(codigo));
    } catch (err: any) {
      setErro(err?.response?.data?.error ?? 'Não consegui aplicar o cupom agora. Tente de novo em instantes.');
      setIndo(false);
    }
  };

  if (!aberto) {
    return <button type="button" className="tp-cupom-abrir" onClick={() => setAberto(true)}>Tem um cupom?</button>;
  }
  return (
    <form className="tp-cupom" onSubmit={aplicar} aria-label="Cupom">
      <label className="tp-cpf">
        Cupom
        <input autoFocus autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={40}
          value={codigo} onChange={ev => setCodigo(ev.target.value)} />
      </label>
      <div className="tp-acoes">
        <button type="submit" className="tp-botao" disabled={indo || codigo.trim().length < 3} aria-busy={indo}>Aplicar</button>
      </div>
      {erro && <p className="tp-erro" role="alert">{erro}</p>}
    </form>
  );
};

const TelaDePlanos: React.FC<{
  situacao: SituacaoDoPlano;
  onAtualizar: (s: SituacaoDoPlano) => void;
  onSair: () => void;
}> = ({ situacao, onAtualizar, onSair }) => {
  const [escolhendo, setEscolhendo] = useState<IdDoPlano | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const dados = Object.fromEntries(situacao.planos.map(p => [p.id, p])) as Record<IdDoPlano, (typeof situacao.planos)[number]>;
  const essencial = dados.essencial;
  const anual = dados.essencial_anual;
  const economia = essencial && anual
    ? Math.round((1 - anual.precoCentavos / (essencial.precoCentavos * 12)) * 100)
    : null;

  // Com o Asaas: o plano escolhido abre a forma de pagar e o CPF, antes de ir para lá.
  const [pagarPeloAsaas, setPagarPeloAsaas] = useState<IdDoPlano | null>(null);
  const [forma, setForma] = useState<'pix' | 'cartao'>('pix');
  const [cpf, setCpf] = useState('');
  const [indo, setIndo] = useState(false);

  // O plano tocado na página /conheca, que atravessou o login: abre já escolhido, uma vez.
  useEffect(() => {
    let veio: string | null = null;
    try { veio = localStorage.getItem('plano-do-cadastro'); localStorage.removeItem('plano-do-cadastro'); } catch { /* bloqueado */ }
    if (veio === 'essencial' || veio === 'essencial_anual' || veio === 'completo') {
      if (situacao.estado !== 'pedido') void escolher(veio);
    }
    // Só ao abrir a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abre embaixo dos cartões: no celular, sem rolar até lá, o toque pareceria não fazer nada.
  useEffect(() => {
    if (!pagarPeloAsaas) return;
    document.getElementById('tp-pagar-asaas')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pagarPeloAsaas]);

  const irPagar = async () => {
    if (!pagarPeloAsaas) return;
    setIndo(true); setErro(null);
    // A aba nasce no clique (senão o navegador bloqueia): ver `abaDePagamento.ts`.
    const aba = abrirAbaDePagamento();
    try {
      const r = await assinarPlano({ plano: pagarPeloAsaas, forma, cpf });
      if (r.tipo === 'link') {
        aba.ir(r.link);
        setPagarPeloAsaas(null);
        setErro(null);
        onAtualizar({ ...situacao, estado: 'pedido', plano: pagarPeloAsaas, podePagar: true });
        return;
      }
      aba.fechar();
      setErro(r.tipo === 'troca-no-vencimento'
        ? 'Combinado: o plano novo começa no seu próximo vencimento.'
        : 'Você já está neste plano.');
      setPagarPeloAsaas(null);
    } catch (e: any) {
      aba.fechar();
      setErro(e?.response?.data?.error ?? 'Não consegui abrir o pagamento agora. Tente de novo em instantes.');
    } finally {
      setIndo(false);
    }
  };

  const escolher = async (id: IdDoPlano) => {
    if (situacao.comAsaas) { setPagarPeloAsaas(id); setErro(null); return; }
    setEscolhendo(id); setErro(null);
    try {
      onAtualizar(await escolherPlano(id));
    } catch {
      setErro('Não consegui registrar o plano agora. Tente de novo em instantes.');
    } finally {
      setEscolhendo(null);
    }
  };

  // O cartão selecionado: começa no plano já escolhido, ou no Completo. Clicar move o
  // destaque. A fita "Mais popular" é outra coisa e fica no Completo.
  const [selecionado, setSelecionado] = useState<IdDoPlano>(
    (situacao.estado === 'pedido' && situacao.plano) ? situacao.plano : 'completo',
  );
  useEffect(() => { if (pagarPeloAsaas) setSelecionado(pagarPeloAsaas); }, [pagarPeloAsaas]);

  const voltou = situacao.estado === 'encerrado';
  const escolhido = situacao.estado === 'pedido' ? situacao.plano : null;

  return (
    <main className="tp">
      <header className="tp-topo">
        <span className="tp-marca"><span className="tp-fin">Fin</span>Tracking</span>
        <button type="button" className="tp-sair" onClick={onSair}>Sair</button>
      </header>

      <h1 className="tp-titulo">{voltou ? 'Seu plano terminou' : 'Escolha o seu plano'}</h1>
      <p className="tp-sub">
        {voltou
          ? 'O que você lançou continua guardado. Escolha um plano para voltar.'
          : 'Sem fidelidade. Cancele quando quiser.'}
      </p>

      {escolhido && situacao.comAsaas && !pagarPeloAsaas && dados[escolhido] && (
        <section className="tp-pagar" aria-label="Pagamento pendente">
          <h2>Falta pagar o {dados[escolhido].nome}</h2>
          <p className="tp-nota">Você escolheu este plano e o pagamento ainda não foi concluído.</p>
          <div className="tp-acoes">
            <button type="button" className="tp-botao" onClick={() => setPagarPeloAsaas(escolhido)}>Continuar o pagamento</button>
          </div>
          <p className="tp-nota">O pagamento abre em outra aba. Se já pagou, esta tela abre o app sozinha assim que o pagamento for confirmado.</p>
        </section>
      )}

      {/* Sem o Asaas não há como pagar: o Pix manual saiu em 23/10. */}
      {escolhido && !situacao.comAsaas && (
        <section className="tp-pagar" aria-label="Pagamento indisponível">
          <h2>Falta pagar o {dados[escolhido]?.nome}</h2>
          <p className="tp-nota">O pagamento ainda não está no ar. Tente de novo mais tarde.</p>
        </section>
      )}

      <div className="tp-grade" role="radiogroup" aria-label="Planos">
        {ORDEM.filter(id => dados[id]).map(id => {
          const p = dados[id];
          const destaque = id === 'completo';
          const ehOEscolhido = escolhido === id;
          const marcado = selecionado === id;
          return (
            <article
              key={id}
              role="radio"
              aria-checked={marcado}
              tabIndex={0}
              onClick={() => setSelecionado(id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelecionado(id); } }}
              className={`tp-plano${destaque ? ' tp-completo' : ''}${marcado ? ' tp-escolhido' : ''}`}
            >
              {destaque && <span className="tp-fita">Mais popular</span>}
              <div className="tp-plano-topo">
                <h3>{p.nome}</h3>
                {destaque && <span className="tp-selo">Open Finance</span>}
                {id === 'essencial_anual' && economia ? <span className="tp-selo tp-selo-ok">{economia}% a menos</span> : null}
              </div>
              <p className="tp-preco">{reais(p.precoCentavos)}<small>/{p.dias >= 365 ? 'ano' : 'mês'}</small></p>
              <p className="tp-preco-alt">
                {id === 'essencial' && 'Pra saber hoje quanto vai sobrar nos próximos meses.'}
                {id === 'completo' && `Pra não lançar nada à mão. Banco a mais: ${reais(situacao.bancoExtraCentavos)} cada.`}
                {id === 'essencial_anual' && <>O Essencial por 12 meses: <strong>{reais(Math.round(p.precoCentavos / 12))} por mês</strong>.</>}
              </p>
              <ul className="tp-lista">
                {id !== 'essencial' && (
                  <li><Check /><span><span className="tp-soma">Tudo do Essencial</span>{id === 'completo' ? ', e mais:' : ''}</span></li>
                )}
                {ITENS[id].map(t => <li key={t}><Check /><span>{t}</span></li>)}
              </ul>
              <button
                type="button"
                className={marcado ? 'tp-botao' : 'tp-botao-2'}
                disabled={escolhendo !== null}
                aria-busy={escolhendo === id}
                onClick={e => { e.stopPropagation(); setSelecionado(id); escolher(id); }}
                aria-pressed={ehOEscolhido}
              >
                {ehOEscolhido ? 'Escolhido' : `Assinar o ${p.nome}`}
              </button>
            </article>
          );
        })}
      </div>

      {pagarPeloAsaas && dados[pagarPeloAsaas] && (
        <section id="tp-pagar-asaas" className="tp-pagar tp-asaas" aria-label="Como pagar">
          <h2>{dados[pagarPeloAsaas].nome}: {reais(dados[pagarPeloAsaas].precoCentavos)}{dados[pagarPeloAsaas].dias >= 365 ? ' por ano' : ' por mês'}</h2>
          <div className="tp-formas" role="radiogroup" aria-label="Forma de pagamento">
            <label className={forma === 'pix' ? 'tp-forma tp-forma-on' : 'tp-forma'}>
              <input type="radio" name="forma" checked={forma === 'pix'} onChange={() => setForma('pix')} /> Pix
            </label>
            <label className={forma === 'cartao' ? 'tp-forma tp-forma-on' : 'tp-forma'}>
              <input type="radio" name="forma" checked={forma === 'cartao'} onChange={() => setForma('cartao')} /> Cartão de crédito
            </label>
          </div>
          <p className="tp-nota">
            {forma === 'cartao'
              ? 'No cartão, a renovação é automática.'
              : 'No Pix, a cobrança de cada mês chega por e-mail, com o QR Code (ou o boleto, se preferir).'}
          </p>
          <label className="tp-cpf">
            CPF
            <input inputMode="numeric" autoComplete="off" placeholder="000.000.000-00" value={cpf}
              onChange={e => setCpf(e.target.value)} maxLength={14} />
          </label>
          <p className="tp-nota">O pagamento é feito no Asaas, que pede o CPF. Ele não fica guardado no FinTracking.</p>
          <div className="tp-acoes">
            <button type="button" className="tp-botao" disabled={indo || cpf.replace(/\D/g, '').length !== 11} aria-busy={indo} onClick={irPagar}>
              Ir para o pagamento
            </button>
            <button type="button" className="tp-botao-2" disabled={indo} onClick={() => setPagarPeloAsaas(null)}>Voltar</button>
          </div>
        </section>
      )}

      {erro && <p className="tp-erro" role="alert">{erro}</p>}

      <Cupom onUsado={onAtualizar} />

      <Motivos />

      {/* A garantia como chamada (22/10; 28/10 virou o teste de 7 dias, com tudo
          de volta). Promete o que o app cumpre — ver para onde vai o dinheiro e
          quanto sobra —, não "resolver as finanças". */}
      <p className="tp-garantia">
        <strong>7 dias para testar.</strong> Em uma semana, o Finn já mostra para onde vai cada real e quanto sobra nos próximos meses. Para cancelar, vá em "Meu plano" e você é ressarcido do valor total.
      </p>

      <details className="tp-condicoes">
        <summary>Pagamento e devolução</summary>
        {situacao.comAsaas
          ? <p>Pix ou cartão. O pagamento é processado pelo Asaas, instituição de pagamento autorizada pelo Banco Central. Os dados do cartão ficam com o Asaas, nunca com o FinTracking.</p>
          : <p>O pagamento ainda não está no ar.</p>}
        <p>{situacao.garantia}</p>
        <p>Dúvidas: <a href="mailto:fintracking.info@gmail.com">fintracking.info@gmail.com</a></p>
      </details>
    </main>
  );
};

export default TelaDePlanos;
